/**
 * Better Auth client for the tenant shell (Auth0 → Better Auth plan, Task 11).
 *
 * Better Auth is served same-origin at `/identity/*` on every tenant host
 * (CloudFront in production, the Vite proxy in dev). The session is a
 * host-only first-party cookie, so every call sends `credentials: "include"`.
 *
 * The session cookie is never sent to the API. `GET /identity/token` turns it
 * into a 15-minute bridge token, and the shell exchanges that token at
 * `POST /user-access/refresh-token` exactly as it exchanges an Auth0 id_token.
 *
 * Every link that leaves the browser (social callback, magic link, password
 * reset, email verification) carries an ABSOLUTE same-origin URL. The emailed
 * GET and the provider callback reach the backend with no `Origin` header, so
 * the backend resolves a relative URL against its fallback host, not this one.
 */
import { createAuthClient } from "better-auth/client";
import { magicLinkClient, twoFactorClient } from "better-auth/client/plugins";

export const IDENTITY_BASE_PATH = "/identity";

/** The shell path that finishes a social or magic-link sign-in. Never under `/identity`, which the edge sends to the API. */
export const IDENTITY_RETURN_PATH = "/signin/return";
/** Better Auth sends the person here as `?token=…` (or `?error=INVALID_TOKEN`). */
export const RESET_PASSWORD_PATH = "/reset-password";

/** Used only when a TERMS_REQUIRED refusal names no version. Mirrors `CURRENT_TERMS_VERSION` in `Services/Identity/terms.ts`. */
export const IDENTITY_TERMS_VERSION = "2.0";

/** Mirrors `metaship-backend` `Services/Identity/identity.interface.ts`. */
export type IdentityMode = "auth0" | "both" | "identity";

export type SocialProvider = "google" | "microsoft";
export type IdentityProvider = "password" | "magicLink" | SocialProvider;
export const ALL_IDENTITY_PROVIDERS: readonly IdentityProvider[] = ["password", "magicLink", "google", "microsoft"];
export const SOCIAL_PROVIDERS: readonly SocialProvider[] = ["google", "microsoft"];

/** The refusals of `GET /identity/token` that the shell has a screen for. */
export type BridgeRefusalCode = "BANNED" | "EMAIL_NOT_VERIFIED" | "TERMS_REQUIRED";

export type BridgeTokenResult =
  | { kind: "token"; token: string }
  | { kind: "refused"; code: BridgeRefusalCode; message: string; termsVersion: string | null };

/** `/identity/token` answered 401: there is no Better Auth session (signed out, expired, or revoked). */
export class IdentitySessionEndedError extends Error {
  constructor(message = "The sign-in session has ended") {
    super(message);
    this.name = "IdentitySessionEndedError";
  }
}

/** `/identity/token` refused the session. The store routes each code to its own screen. */
export class IdentityRefusalError extends Error {
  constructor(
    readonly code: BridgeRefusalCode,
    message: string,
    readonly termsVersion: string | null = null,
  ) {
    super(message);
    this.name = "IdentityRefusalError";
  }
}

/** A Better Auth call failed. `code` is Better Auth's error code when it sent one. */
export interface IdentityError {
  code: string;
  message: string;
  status: number;
}

const TOKEN_TIMEOUT_MS = 10 * 1000;
const MODE_TIMEOUT_MS = 3 * 1000;
const SIGN_OUT_TIMEOUT_MS = 3 * 1000;
const REFUSAL_CODES: ReadonlySet<string> = new Set(["BANNED", "EMAIL_NOT_VERIFIED", "TERMS_REQUIRED"]);

function origin(): string {
  return window.location.origin;
}

export const authClient = createAuthClient({
  baseURL: typeof window !== "undefined" ? window.location.origin : "http://localhost",
  basePath: IDENTITY_BASE_PATH,
  fetchOptions: { credentials: "include" },
  plugins: [magicLinkClient(), twoFactorClient()],
});

/**
 * A same-origin PATH, or "/" when the value is anything else. Rejects
 * absolute URLs, protocol-relative `//host` and `/\host` (browsers read a
 * backslash as a slash), control characters, and paths that the edge sends to
 * the API or that would loop back into the return handler.
 */
export function safeReturnTo(raw: string | null | undefined): string {
  if (/[\u0000-\u001f\u007f]/.test(raw ?? "")) return "/";
  const value = (raw ?? "").trim();
  if (!value.startsWith("/")) return "/";
  if (value.startsWith("//") || value.includes("\\")) return "/";
  let parsed: URL;
  try {
    parsed = new URL(value, "https://shell.invalid");
  } catch {
    return "/";
  }
  if (parsed.origin !== "https://shell.invalid") return "/";
  const path = parsed.pathname;
  if (path === IDENTITY_BASE_PATH || path.startsWith(`${IDENTITY_BASE_PATH}/`)) return "/";
  if (path === IDENTITY_RETURN_PATH || path === RESET_PASSWORD_PATH) return "/";
  return `${path}${parsed.search}${parsed.hash}`;
}

/** The absolute URL a social or magic-link sign-in comes back to. */
export function identityReturnUrl(returnTo?: string | null): string {
  const path = safeReturnTo(returnTo);
  const query = path === "/" ? "" : `?returnTo=${encodeURIComponent(path)}`;
  return `${origin()}${IDENTITY_RETURN_PATH}${query}`;
}

/** The absolute URL the password-reset email sends the person to. */
export function resetPasswordUrl(): string {
  return `${origin()}${RESET_PASSWORD_PATH}`;
}

/** `GET /identity/mode`. Any failure, or an answer the shell does not know, is "auth0" (R3). */
export async function fetchIdentityMode(): Promise<IdentityMode> {
  try {
    const res = await fetch(`${origin()}${IDENTITY_BASE_PATH}/mode`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(MODE_TIMEOUT_MS),
    });
    if (!res.ok) return "auth0";
    const body = (await res.json()) as { mode?: unknown };
    return body?.mode === "both" || body?.mode === "identity" ? body.mode : "auth0";
  } catch {
    return "auth0";
  }
}

async function requestBridgeToken(): Promise<BridgeTokenResult | null> {
  const res = await fetch(`${origin()}${IDENTITY_BASE_PATH}/token`, {
    credentials: "include",
    headers: { Accept: "application/json" },
    signal: AbortSignal.timeout(TOKEN_TIMEOUT_MS),
  });
  if (res.status === 401) return null;
  const body = (await res.json().catch(() => null)) as {
    token?: unknown;
    code?: unknown;
    message?: unknown;
    termsVersion?: unknown;
  } | null;
  if (res.status === 403 && typeof body?.code === "string" && REFUSAL_CODES.has(body.code)) {
    return {
      kind: "refused",
      code: body.code as BridgeRefusalCode,
      message: typeof body.message === "string" ? body.message : body.code,
      termsVersion: typeof body.termsVersion === "string" ? body.termsVersion : null,
    };
  }
  if (!res.ok || typeof body?.token !== "string" || !body.token) {
    // A 5xx or a malformed answer is transient. The caller retries; it does not sign out.
    throw new Error(`GET ${IDENTITY_BASE_PATH}/token failed: ${res.status}`);
  }
  return { kind: "token", token: body.token };
}

let inFlight: Promise<BridgeTokenResult | null> | null = null;

/**
 * The bridge token for the current Better Auth session. Calls that overlap
 * share ONE request (single flight). Returns null when there is no session
 * (401), a refusal for the gate codes, and throws on a transient failure.
 */
export function getBridgeToken(): Promise<BridgeTokenResult | null> {
  if (!inFlight) {
    inFlight = requestBridgeToken().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** `POST /identity/accept-terms`. Throws when the backend does not record the acceptance. */
export async function acceptIdentityTerms(termsVersion: string): Promise<void> {
  const res = await fetch(`${origin()}${IDENTITY_BASE_PATH}/accept-terms`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ termsVersion }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: unknown } | null;
    throw new Error(typeof body?.message === "string" ? body.message : `POST ${IDENTITY_BASE_PATH}/accept-terms failed: ${res.status}`);
  }
}

/** Start Google or Microsoft sign-in. On success the browser leaves this page. */
export async function signInWithProvider(provider: SocialProvider, returnTo?: string | null): Promise<IdentityError | null> {
  const callbackURL = identityReturnUrl(returnTo);
  const { error } = await authClient.signIn.social({
    provider,
    callbackURL,
    errorCallbackURL: callbackURL,
  });
  return error ? toIdentityError(error) : null;
}

/**
 * End the Better Auth session. Best effort and bounded: a sign-out must never
 * hang on the network, and the store clears local state either way.
 */
export async function signOutEverywhere(): Promise<void> {
  try {
    await authClient.signOut({ fetchOptions: { signal: AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS) } });
  } catch {
    // The cookie expires on its own. Local state is cleared by the caller.
  }
}

/** Send the verification email again. The link signs the person in and comes back to the shell. */
export async function sendVerificationLink(email: string): Promise<IdentityError | null> {
  const { error } = await authClient.sendVerificationEmail({ email, callbackURL: identityReturnUrl() });
  return error ? toIdentityError(error) : null;
}

/** The email of the current Better Auth session, or null. */
export async function sessionEmail(): Promise<string | null> {
  try {
    const { data } = await authClient.getSession();
    return data?.user?.email ?? null;
  } catch {
    return null;
  }
}

export function toIdentityError(error: { code?: string; message?: string; status?: number; statusText?: string }): IdentityError {
  return {
    code: error.code || (error.status === 429 ? "TOO_MANY_REQUESTS" : "UNKNOWN"),
    message: error.message || error.statusText || "",
    status: error.status ?? 0,
  };
}
